import { useCallback, useMemo } from 'react';
import { BuilderResponse } from 'src/views/uiflow/util/helpers/useUIFlowQueries';
import usePermission from './usePermission';
import { AppDispatch } from 'src/store';
import { useAppDispatch, useAppSelector } from 'src/store/baseHooks';
import { useRouter } from 'next/router';
import {
  PermissionType,
  UiComponentPermissions,
  UiFlowPermissionsDataState,
  resetActiveUiFlowPermissions,
  setActiveUiFlowPermissions
} from 'src/store/slices/uiFlowPermissionsDataSlice';

/**
 * Permissions object in the access token has the structure of '<menuItemComponent.code>.<uiComponent.name>.<permissionType>'.
 * For example, 'order.main order data panel.CanRead'.
 *
 * UIFlows which have a link in the menu should have a url of /uiflow/<uiflow.code>.  If a match is not found then access is denied.
 * However, if a match is found, we can use an array of all uicomponents associated with the uiflow along with the data in the menu item to get what we need to build the permissions object which
 * can be seen in the redux store.
 *
 * TODO: handle the case where a uiflow is not linked to a menu item but maybe being called from a link on a page.
 *
 *
 */

const useUIFlowPermissions = () => {
  const router = useRouter();
  const { hasPermission, findItemObject, flattenMenuItems, menuItems } = usePermission();
  const dispatch: AppDispatch = useAppDispatch();

  const ALLOWALL = process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' && process.env.NEXT_PUBLIC_PERMISSION_OVERRIDE;

  const flowComponentPermissions = useAppSelector(state => state.uiFlowPermissions.uiComponentPermissions);

  function getAfterThirdSlash(inputString: string) {
    // Split the string by slashes
    const parts = inputString.split('/');

    // Check if there are at least 4 parts (3 slashes)
    if (parts.length >= 4) {
      // Join everything after the third slash
      const result = parts.slice(3).join('/');

      return result;
    } else {
      return 'Not enough slashes in the input string';
    }
  }

  /**
   * Get the menu component code for the current flow
   * - When a uiflow attempts to load, we check that the url path is present in one of the menu items in the menu.
   * - If it is, we get the menu item object and extract the id from it, then get the menu item component by id, and return the code.
   * - If it isn't, we return an empty string.
   *
   * @returns Promise<string>
   */
  const getMenuComponentCodeForFlow = useCallback(async () => {
    let path = router.asPath;

    // if we are in a UI module,  we actually want to check we have permisions for the uiflow not the uimodule
    // but admin centre is an exception at the moment
    if (
      path.includes('/uimodule/') &&
      !path.includes('AdminCentre') &&
      !path.includes('Warehouse') &&
      !path.includes('Reports')
    ) {
      path = '/uiflow/' + getAfterThirdSlash(path);

      return getAfterThirdSlash(path);
    }

    /** Get all menu items which have a path property, including any which are children */
    const flatMenuItems = flattenMenuItems(menuItems);

    /** Find a menu object which matches the curremt URL path */
    const menuItemObject = flatMenuItems.find((item: any) => {
      return findItemObject(item, path);
    });

    /**
     * Do we have a menuItem object?
     * If so,
     *  - destructure the id from object
     *  - get menuItem by id
     *  - get menuItemComponent by id
     *  - return menuItemComponent.code
     * If there's an error, return empty string
     */
    if (menuItemObject) {
      return (menuItemObject.menuItemComponentCode ?? '-').toLowerCase();
    }

    return '-';
  }, [router, menuItems, flattenMenuItems, findItemObject]);

  /**
   * Deregister the permissions for the given uiflow
   */
  const deregisterFunction = useCallback(() => {
    dispatch(resetActiveUiFlowPermissions({}));
  }, [dispatch]);

  /**
   * Register the permissions for the given uiflow
   *
   * @param uiflow BuilderResponse
   * @returns () => void
   */
  const registerUIFlowPermissions = useCallback(
    async (uiflow?: BuilderResponse) => {
      const res = await getMenuComponentCodeForFlow();

      const code = res ?? '-'; // Set code to a hyphen if undefined, this ensures we don't match an permissions without a code.

      const {
        id = '',
        dtoId = '',
        initialUIComponent = {},
        steps: { addSteps = [], editSteps = [] } = {}
      } = uiflow ?? {};

      let { displayName = '' } = uiflow ?? {};

      if (displayName === 'Consignments') {
        displayName = 'order';
      }

      const permissionTypes: PermissionType[] = ['CanCreate', 'CanRead', 'CanUpdate', 'CanDelete'];

      const componentPermissionsById: Record<string, UiComponentPermissions> = {};

      const uiComponents = [
        ...(uiflow ? [initialUIComponent] : []),
        ...addSteps.map((step: any) => ({ ...step?.uicomponent, isAddStep: true })),
        ...editSteps.map((step: any) => ({ ...step?.uicomponent, isAddStep: false }))
      ];

      for (const uiComponent of uiComponents) {
        const { id, name, isAddStep } = uiComponent;
        const permissions: Partial<Record<PermissionType, boolean>> = {};

        for (const permissionType of permissionTypes) {
          // if we are an add flow the canCreate on the list will determine add flow component permissions
          if (isAddStep || displayName === 'Users') {
            permissions[permissionType] = true;
          } else {
            const permission = hasPermission(`${code}.${(name ?? '').toLowerCase()}`, permissionType);
            permissions[permissionType] = permission;
          }
        }

        componentPermissionsById[id] = permissions as Record<PermissionType, boolean>;
      }

      const uiFlowPermissionsObject: UiFlowPermissionsDataState = {
        uiFlowId: id,
        uiFlowDescription: displayName,
        dtoId: dtoId,
        uiComponentPermissions: componentPermissionsById
      };

      dispatch(setActiveUiFlowPermissions(uiFlowPermissionsObject));

      return { deregisterFunction, uiFlowPermissionsObject };
    },
    [getMenuComponentCodeForFlow, dispatch, deregisterFunction, hasPermission]
  );

  /**
   * Get the permissions for the given ui component
   *
   * @param uiComponentId
   * @returns UiComponentPermissions
   */
  const getPermissionsForUiComponent = useCallback(
    (uiComponentId: string): UiComponentPermissions => {
      if (ALLOWALL) {
        return {
          CanCreate: true,
          CanRead: true,
          CanUpdate: true,
          CanDelete: true
        };
      }

      return flowComponentPermissions[uiComponentId] ?? {};
    },
    [flowComponentPermissions, ALLOWALL]
  );

  /**
   * Get the permission for the given ui component and permission type
   *
   * @param uiComponentId string
   * @param permissionType PermissionType
   * @returns boolean
   */
  const getUiComponentPermissionByType = useCallback(
    (uiComponentId: string, permissionType: PermissionType): boolean => {
      if (ALLOWALL) return true;

      const permissionObject = flowComponentPermissions[uiComponentId];
      const permission = permissionObject?.[permissionType];

      return permission;
    },
    [flowComponentPermissions, ALLOWALL]
  );

  return useMemo(
    () => ({
      registerUIFlowPermissions,
      getPermissionsForUiComponent,
      getUiComponentPermissionByType
    }),
    [getPermissionsForUiComponent, getUiComponentPermissionByType, registerUIFlowPermissions]
  );
};

export default useUIFlowPermissions;
